import { useState, useEffect } from 'react'
import { supabase } from './supabase'
import BundlePicker from './BundlePicker'

const SECTIONS = [
  { key: 'dinner', label: 'Dinner', emoji: '🍽️' },
  { key: 'lunch', label: 'Lunch', emoji: '🥗' },
  { key: 'breakfast', label: 'Breakfast', emoji: '🌅' },
  { key: 'snacks', label: 'Snacks', emoji: '🍿' },
  { key: 'misc', label: 'Misc', emoji: '🛒' },
]

function getWeekStart(offset = 0) {
  const d = new Date()
  const day = d.getDay()
  d.setDate(d.getDate() - (day === 0 ? 6 : day - 1) + offset * 7)
  d.setHours(0, 0, 0, 0)
  return d
}

function weekLabel(offset) {
  if (offset === 0) return 'This week'
  if (offset === -1) return 'Last week'
  const d = getWeekStart(offset)
  const end = new Date(d)
  end.setDate(end.getDate() + 6)
  const fmt = { month: 'short', day: 'numeric' }
  return `${d.toLocaleDateString('en-US', fmt)} – ${end.toLocaleDateString('en-US', fmt)}`
}

function weekRange(dateStr) {
  const d = new Date(dateStr + 'T00:00:00')
  const end = new Date(d)
  end.setDate(end.getDate() + 6)
  const fmt = { month: 'short', day: 'numeric' }
  return `${d.toLocaleDateString('en-US', fmt)} – ${end.toLocaleDateString('en-US', fmt)}`
}

export default function Plan({ user, onBack, onGoToCart }) {
  const [weekOffset, setWeekOffset] = useState(0)
  const [view, setView] = useState('week')
  const [historyTab, setHistoryTab] = useState('plans')
  const [expanded, setExpanded] = useState(new Set(['dinner']))
  const [entries, setEntries] = useState([])
  const [members, setMembers] = useState([])
  const [householdId, setHouseholdId] = useState(null)
  const [myBundles, setMyBundles] = useState([])
  const [pickerSection, setPickerSection] = useState(null)
  const [loading, setLoading] = useState(true)
  const [nudged, setNudged] = useState(false)
  const [pastPlans, setPastPlans] = useState([])

  const weekStartStr = getWeekStart(weekOffset).toISOString().split('T')[0]

  useEffect(() => { init() }, [])

  useEffect(() => {
    if (householdId) fetchEntries()
  }, [weekOffset, householdId])

  useEffect(() => {
    if (view === 'history' && householdId) fetchPastPlans()
  }, [view, householdId])

  async function init() {
    const { data: membership } = await supabase
      .from('household_members').select('household_id')
      .eq('user_id', user.id).maybeSingle()
    if (!membership) { setLoading(false); return }
    setHouseholdId(membership.household_id)

    const [{ data: memberRows }, { data: bundles }] = await Promise.all([
      supabase.from('household_members').select('user_id').eq('household_id', membership.household_id),
      supabase.from('bundles').select('*').eq('household_id', membership.household_id).order('created_at', { ascending: false }),
    ])
    setMyBundles(bundles || [])

    if (memberRows) {
      const profiles = await Promise.all(
        memberRows.map(m => supabase.from('profiles').select('id, name').eq('id', m.user_id).maybeSingle())
      )
      setMembers(profiles.map(r => r.data).filter(Boolean))
    }
    setLoading(false)
  }

  async function fetchEntries() {
    const { data } = await supabase
      .from('meal_plan_entries')
      .select('*, bundles(*)')
      .eq('household_id', householdId)
      .eq('week_start', weekStartStr)
    setEntries(data || [])
  }

  async function fetchPastPlans() {
    const currentWeek = getWeekStart(0).toISOString().split('T')[0]
    const { data } = await supabase
      .from('meal_plan_entries')
      .select('id, bundle_id, week_start, section, days, bundles(id, name, emoji)')
      .eq('household_id', householdId)
      .lt('week_start', currentWeek)
      .order('week_start', { ascending: false })
    const grouped = {}
    ;(data || []).forEach(e => {
      if (!grouped[e.week_start]) grouped[e.week_start] = []
      grouped[e.week_start].push(e)
    })
    setPastPlans(Object.entries(grouped).map(([week, es]) => ({ week, entries: es })))
  }

  function toggleSection(key) {
    setExpanded(prev => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }

  async function addEntry(bundleId, days) {
    const { data, error } = await supabase.from('meal_plan_entries').insert({
      household_id: householdId,
      bundle_id: bundleId,
      week_start: weekStartStr,
      section: pickerSection,
      days,
      added_by: user.id,
    })
    console.log('insert result:', data, error)
    console.log('householdId:', householdId, 'bundleId:', bundleId, 'week:', weekStartStr, 'section:', pickerSection)
    setPickerSection(null)
    fetchEntries()
  }

  async function removeEntry(id) {
    await supabase.from('meal_plan_entries').delete().eq('id', id)
    setEntries(prev => prev.filter(e => e.id !== id))
  }

  async function shopPlan() {
    const bundleIds = [...new Set(entries.map(e => e.bundle_id))]
    if (!bundleIds.length) return
    await supabase.from('bundles').update({ in_cart: true }).in('id', bundleIds)
    onGoToCart()
  }

  async function repeatPlan(oldEntries) {
    await supabase.from('meal_plan_entries').insert(
      oldEntries.map(e => ({
        household_id: householdId,
        bundle_id: e.bundle_id,
        week_start: getWeekStart(0).toISOString().split('T')[0],
        section: e.section,
        days: e.days,
        added_by: user.id,
      }))
    )
    setWeekOffset(0)
    setView('week')
  }

  async function clearPlan() {
    await supabase.from('meal_plan_entries').delete().eq('household_id', householdId).eq('week_start', weekStartStr)
    setEntries([])
  }

  function nudge() {
    setNudged(true)
    setTimeout(() => setNudged(false), 2000)
  }

  const entriesBySection = Object.fromEntries(
    SECTIONS.map(s => [s.key, entries.filter(e => e.section === s.key)])
  )

  if (loading) return (
    <div style={styles.container}>
      <div style={styles.navBar}>
        <button style={styles.backBtn} onClick={onBack}>← Back</button>
        <div style={styles.navTitle}>Plan</div>
        <div style={{ width: 44 }} />
      </div>
      <div style={styles.loadingWrap}><span style={styles.loadingText}>Loading…</span></div>
    </div>
  )

  return (
    <div style={styles.container}>
      <div style={styles.navBar}>
        <button style={styles.backBtn} onClick={onBack}>← Back</button>
        <div style={styles.navTitle}>Plan</div>
        {entries.length > 0 && view === 'week' ? (
          <button style={styles.clearBtn} onClick={clearPlan}>Clear</button>
        ) : (
          <div style={{ width: 44 }} />
        )}
      </div>

      <div style={styles.weekNav}>
        <button style={styles.weekArrow} onClick={() => setWeekOffset(w => w - 1)}>‹</button>
        <span style={styles.weekLabel}>{weekLabel(weekOffset)}</span>
        <button
          style={{ ...styles.weekArrow, ...(weekOffset >= 0 ? styles.weekArrowDim : {}) }}
          onClick={() => setWeekOffset(w => Math.min(0, w + 1))}
        >›</button>
      </div>

      <div style={styles.toggleRow}>
        <button style={{ ...styles.toggleBtn, ...(view === 'week' ? styles.toggleBtnActive : {}) }} onClick={() => setView('week')}>
          This week
        </button>
        <button style={{ ...styles.toggleBtn, ...(view === 'history' ? styles.toggleBtnActive : {}) }} onClick={() => setView('history')}>
          History
        </button>
      </div>

      {view === 'week' ? (
        <>
          {members.length > 1 && (
            <div style={styles.buildingBar}>
              <div style={styles.avatarRow}>
                {members.map((m, i) => (
                  <div key={i} style={{ ...styles.avatar, marginLeft: i > 0 ? '-6px' : 0 }}>
                    {m.name?.[0]?.toUpperCase()}
                  </div>
                ))}
                <span style={styles.buildingText}>Building together</span>
              </div>
              <button style={styles.nudgeBtn} onClick={nudge}>
                {nudged ? '✓ Nudged!' : 'Nudge'}
              </button>
            </div>
          )}

          <div style={styles.content}>
            {SECTIONS.map(section => {
              const sectionEntries = entriesBySection[section.key] || []
              const isOpen = expanded.has(section.key)
              return (
                <div key={section.key} style={styles.sectionCard}>
                  <button style={styles.sectionHeader} onClick={() => toggleSection(section.key)}>
                    <span style={styles.sectionEmoji}>{section.emoji}</span>
                    <span style={styles.sectionName}>{section.label}</span>
                    {sectionEntries.length > 0 && (
                      <span style={styles.sectionBadge}>{sectionEntries.length}</span>
                    )}
                    <span style={styles.chevron}>{isOpen ? '▾' : '▸'}</span>
                  </button>

                  {isOpen && (
                    <div style={styles.sectionBody}>
                      {sectionEntries.map(entry => (
                        <EntryRow
                          key={entry.id}
                          entry={entry}
                          members={members}
                          onRemove={() => removeEntry(entry.id)}
                        />
                      ))}
                      <button style={styles.addBundleBtn} onClick={() => setPickerSection(section.key)}>
                        + Add a bundle
                      </button>
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {entries.length > 0 && (
            <div style={styles.shopBar} onClick={shopPlan}>
              <span style={styles.shopText}>Shop this week's plan</span>
              <span style={styles.shopCount}>{entries.length} bundle{entries.length !== 1 ? 's' : ''}</span>
            </div>
          )}
        </>
      ) : (
        <div style={styles.content}>
          <div style={styles.histTabs}>
            <button style={{ ...styles.histTab, ...(historyTab === 'plans' ? styles.histTabActive : {}) }} onClick={() => setHistoryTab('plans')}>
              Past plans
            </button>
            <button style={{ ...styles.histTab, ...(historyTab === 'orders' ? styles.histTabActive : {}) }} onClick={() => setHistoryTab('orders')}>
              Orders
            </button>
          </div>

          {historyTab === 'plans' ? (
            pastPlans.length === 0 ? (
              <div style={styles.emptyHistory}>
                <div style={{ fontSize: '32px', marginBottom: '10px' }}>📅</div>
                <div>No past plans yet.</div>
              </div>
            ) : (
              pastPlans.map(({ week, entries: wEntries }) => (
                <div key={week} style={styles.pastCard}>
                  <div style={styles.pastHeader}>
                    <span style={styles.pastWeek}>{weekRange(week)}</span>
                    <button style={styles.useAgainBtn} onClick={() => repeatPlan(wEntries)}>
                      Use again
                    </button>
                  </div>
                  <div style={styles.pastBundles}>
                    {wEntries.slice(0, 5).map((e, i) => (
                      <span key={i} style={styles.pastBundleChip}>
                        {e.bundles?.emoji} {e.bundles?.name}
                      </span>
                    ))}
                    {wEntries.length > 5 && (
                      <span style={styles.pastBundleChip}>+{wEntries.length - 5} more</span>
                    )}
                  </div>
                </div>
              ))
            )
          ) : (
            <div style={styles.emptyHistory}>
              <div style={{ fontSize: '32px', marginBottom: '10px' }}>🛒</div>
              <div>Order history coming soon.</div>
            </div>
          )}
        </div>
      )}

      {pickerSection && (
        <BundlePicker
          bundles={myBundles}
          defaultTab={pickerSection}
          onAdd={addEntry}
          onClose={() => setPickerSection(null)}
        />
      )}
    </div>
  )
}

function EntryRow({ entry, members, onRemove }) {
  const bundle = entry.bundles
  const days = entry.days || []
  const addedBy = members.find(m => m.id === entry.added_by)

  return (
    <div style={styles.entryRow}>
      <span style={styles.entryEmoji}>{bundle?.emoji || '🛒'}</span>
      <div style={styles.entryInfo}>
        <div style={styles.entryName}>{bundle?.name}</div>
        <div style={styles.entryDays}>
          {days.length === 0
            ? <span style={styles.noDays}>No days set</span>
            : days.length === 7
              ? <span style={styles.dayChip}>All week</span>
              : days.map(d => <span key={d} style={styles.dayChip}>{d}</span>)
          }
        </div>
      </div>
      {addedBy && (
        <div style={styles.addedByDot} title={addedBy.name}>
          {addedBy.name?.[0]?.toUpperCase()}
        </div>
      )}
      <button style={styles.removeBtn} onClick={onRemove}>✕</button>
    </div>
  )
}

const styles = {
  container: {
    maxWidth: '430px', margin: '0 auto', minHeight: '100vh',
    background: 'white', display: 'flex', flexDirection: 'column',
    fontFamily: "'DM Sans', sans-serif",
  },
  navBar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '12px 20px 8px', flexShrink: 0,
  },
  backBtn: {
    fontSize: '13px', color: '#1D9E75', background: 'none',
    border: 'none', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
  },
  clearBtn: {
    fontSize: '13px', color: '#aaa', background: 'none',
    border: 'none', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
  },
  navTitle: { fontFamily: "'DM Serif Display', serif", fontSize: '18px', color: '#1a1a18' },
  loadingWrap: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  loadingText: { fontSize: '14px', color: '#aaa' },
  weekNav: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '0 16px 8px', flexShrink: 0,
  },
  weekArrow: {
    fontSize: '24px', color: '#888', background: 'none', border: 'none',
    cursor: 'pointer', padding: '4px 8px', lineHeight: 1,
  },
  weekArrowDim: { opacity: 0.25, cursor: 'default' },
  weekLabel: { fontSize: '14px', fontWeight: '500', color: '#1a1a18' },
  toggleRow: {
    display: 'flex', margin: '0 20px 12px', background: '#f9f9f7',
    borderRadius: '10px', padding: '3px', flexShrink: 0,
  },
  toggleBtn: {
    flex: 1, padding: '7px', border: 'none', borderRadius: '8px',
    fontFamily: "'DM Sans', sans-serif", fontSize: '12px', color: '#888',
    background: 'none', cursor: 'pointer',
  },
  toggleBtnActive: { background: 'white', color: '#1a1a18', fontWeight: '500', border: '0.5px solid #eee' },
  buildingBar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    margin: '0 20px 10px', padding: '10px 12px',
    background: '#f9f9f7', borderRadius: '12px', border: '0.5px solid #eee', flexShrink: 0,
  },
  avatarRow: { display: 'flex', alignItems: 'center', gap: '6px' },
  avatar: {
    width: '26px', height: '26px', borderRadius: '50%',
    background: '#E1F5EE', color: '#0F6E56',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '11px', fontWeight: '600', border: '1.5px solid white', flexShrink: 0,
  },
  buildingText: { fontSize: '12px', color: '#888', marginLeft: '4px' },
  nudgeBtn: {
    padding: '6px 12px', background: 'none', border: '0.5px solid #ddd',
    borderRadius: '20px', fontSize: '12px', color: '#888',
    cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
  },
  content: { flex: 1, overflowY: 'auto', padding: '0 20px 16px' },
  sectionCard: {
    border: '0.5px solid #eee', borderRadius: '14px',
    overflow: 'hidden', marginBottom: '8px',
  },
  sectionHeader: {
    width: '100%', display: 'flex', alignItems: 'center', gap: '8px',
    padding: '12px 14px', background: 'none', border: 'none',
    cursor: 'pointer', textAlign: 'left', fontFamily: "'DM Sans', sans-serif",
  },
  sectionEmoji: { fontSize: '16px', flexShrink: 0 },
  sectionName: { flex: 1, fontSize: '14px', fontWeight: '500', color: '#1a1a18' },
  sectionBadge: {
    fontSize: '11px', color: 'white', background: '#1D9E75',
    borderRadius: '20px', padding: '1px 7px', fontWeight: '600',
  },
  chevron: { fontSize: '12px', color: '#aaa' },
  sectionBody: { padding: '0 14px 12px', borderTop: '0.5px solid #eee' },
  entryRow: {
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '10px 0', borderBottom: '0.5px solid #f5f5f5',
  },
  entryEmoji: { fontSize: '20px', width: '28px', textAlign: 'center', flexShrink: 0 },
  entryInfo: { flex: 1, minWidth: 0 },
  entryName: { fontSize: '13px', fontWeight: '500', color: '#1a1a18', marginBottom: '4px' },
  entryDays: { display: 'flex', flexWrap: 'wrap', gap: '4px' },
  dayChip: {
    fontSize: '10px', fontWeight: '500', color: '#0F6E56',
    background: '#E1F5EE', borderRadius: '20px', padding: '2px 7px',
  },
  noDays: { fontSize: '10px', color: '#ccc' },
  addedByDot: {
    width: '22px', height: '22px', borderRadius: '50%',
    background: '#f9f9f7', border: '0.5px solid #eee',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '10px', fontWeight: '600', color: '#888', flexShrink: 0,
  },
  removeBtn: {
    background: 'none', border: 'none', fontSize: '11px', color: '#ccc',
    cursor: 'pointer', padding: '4px', flexShrink: 0,
    fontFamily: "'DM Sans', sans-serif",
  },
  addBundleBtn: {
    marginTop: '10px', width: '100%', padding: '10px',
    background: '#f9f9f7', border: '0.5px dashed #ddd', borderRadius: '10px',
    fontSize: '13px', color: '#888', cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
  },
  shopBar: {
    margin: '6px 20px 14px', padding: '14px 18px', background: '#1D9E75',
    borderRadius: '14px', display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', cursor: 'pointer', flexShrink: 0,
  },
  shopText: { fontSize: '14px', fontWeight: '500', color: 'white' },
  shopCount: { fontSize: '12px', color: 'rgba(255,255,255,0.8)' },
  histTabs: { display: 'flex', gap: '6px', marginBottom: '16px' },
  histTab: {
    padding: '8px 16px', borderRadius: '20px', border: '0.5px solid #ddd',
    background: 'none', fontSize: '13px', color: '#888',
    cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
  },
  histTabActive: { background: '#1a1a18', color: 'white', borderColor: '#1a1a18' },
  emptyHistory: {
    flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', padding: '48px 20px',
    fontSize: '13px', color: '#aaa', textAlign: 'center',
  },
  pastCard: {
    border: '0.5px solid #eee', borderRadius: '14px',
    padding: '14px', marginBottom: '10px',
  },
  pastHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px',
  },
  pastWeek: { fontSize: '13px', fontWeight: '500', color: '#1a1a18' },
  useAgainBtn: {
    padding: '6px 12px', background: '#E1F5EE', color: '#0F6E56',
    border: 'none', borderRadius: '20px', fontSize: '12px', fontWeight: '500',
    cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
  },
  pastBundles: { display: 'flex', flexWrap: 'wrap', gap: '6px' },
  pastBundleChip: {
    fontSize: '12px', color: '#888', background: '#f9f9f7',
    borderRadius: '8px', padding: '4px 8px',
  },
}
